import { useState } from 'react';
import { createPortal } from 'react-dom';
import { trpc } from '@tg/app-core';
import { dayHas } from '@tg/domain';
import { RU, addDays, dm, dowMon } from './dates';
import { ini } from './Constructor';
import { useClients } from '../lib/data';
import { Cav } from '../ui/Cav';
import { DatePicker } from '../calendar/Calendar';

export interface Conflict {
  clientId: string;
  date: string;
  title: string;
}
/** Решение по занятому дню: заменить (true), пропустить (false) или перенести тренировку группы на дату */
export type Decision = boolean | string;
/** Ключ — «clientId|дата»: у одного участника может быть занято несколько дней, по каждому своё решение */
export type Decisions = Record<string, Decision>;
export const conflictKey = (r: { clientId: string; date: string }) => r.clientId + '|' + r.date;
const DW = (d: string) => RU[dowMon(d)] + ' ' + dm(d);

/* Окно занятых дней при публикации в группу (GRP-3): заменить, пропустить или перенести на дату; по умолчанию — заменить.
   «Перенести»: своя тренировка участника остаётся, тренировка группы встаёт ему на выбранную дату. */
export function GroupConflicts({ list, names, onDone }: { list: Conflict[]; names: Record<string, string>; onDone: (d: Decisions | null) => void }) {
  const cs = useClients().data;
  const utils = trpc.useUtils();
  const [ch, setCh] = useState<Decisions>(() => Object.fromEntries(list.map((r) => [conflictKey(r), true])));
  const [pick, setPick] = useState<{ r: Conflict; el: HTMLElement } | null>(null);
  /* На дату переноса у участника тоже есть тренировка — она заменится: название для подсказки */
  const [busy, setBusy] = useState<Record<string, string>>({});
  const people = new Set(list.map((r) => r.clientId)).size;
  const n = list.length;
  const all = (v: Decision) => setCh(Object.fromEntries(list.map((r) => [conflictKey(r), v])));

  const moveTo = async (r: Conflict, d: string) => {
    const k = conflictKey(r);
    setCh((c) => ({ ...c, [k]: d }));
    const rows = await utils.trainer.days.fetch({ who: { clientId: r.clientId }, from: d, to: d });
    const x = rows.find((y) => y.date === d);
    setBusy((b) => ({ ...b, [k]: x && dayHas(x.work ?? x.content) ? x.content.title || 'Без названия' : '' }));
  };

  return createPortal(
    <div className="ov on gconf" onClick={(e) => e.target === e.currentTarget && onDone(null)}>
      <div className="md ask gcm">
        <div className="mdh">
          <span className="dot"></span>
          <h2>{people === 1 ? 'У участника уже есть тренировка' : 'У ' + people + ' участников уже есть тренировки'}</h2>
          <button className="cls" onClick={() => onDone(null)}>
            ✕
          </button>
        </div>
        <div className="mdb">
          <p className="lead">
            Тренировка группы встанет всем участникам.{' '}
            {n === 1 ? 'На этот день у него стоит другая — заменить её, пропустить или перенести тренировку группы на другую дату?' : 'На эти дни у них стоят другие — по каждому решите: заменить, пропустить или перенести на другую дату.'}
          </p>
          <div className="gc-list">
            {list.map((r) => {
              const k = conflictKey(r);
              const v = ch[k];
              const moved = typeof v === 'string' ? v : null;
              return (
                <div className="gc-r" key={k}>
                  <Cav ini={ini(names[r.clientId] || '')} photo={cs?.find((c) => c.id === r.clientId)?.photo} />
                  <span className="gc-t">
                    <b>{names[r.clientId] || 'Клиент'}</b>
                    <s>
                      {DW(r.date)} · {r.title}
                    </s>
                    {moved && (
                      <s className="gc-mv">
                        Группа встанет на{' '}
                        <button className="gc-d" onClick={(e) => setPick({ r, el: e.currentTarget })}>
                          {DW(moved)}
                        </button>
                        {busy[k] ? <em> — там «{busy[k]}», заменится</em> : null}
                      </s>
                    )}
                  </span>
                  <span className="seg">
                    <button className={v === true ? 'on' : ''} onClick={() => setCh({ ...ch, [k]: true })}>
                      Заменить
                    </button>
                    <button className={v === false ? 'on' : ''} onClick={() => setCh({ ...ch, [k]: false })}>
                      Пропустить
                    </button>
                    <button className={moved ? 'on' : ''} title="Своя тренировка останется, тренировка группы встанет на выбранную дату" onClick={(e) => setPick({ r, el: e.currentTarget })}>
                      Перенести
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
          {n > 1 && (
            <div className="gc-all">
              <button onClick={() => all(true)}>Заменить у всех</button>
              <button onClick={() => all(false)}>Пропустить всех</button>
            </div>
          )}
          <p className="sub">Пропущенный сохранит свою тренировку; поставить ему тренировку группы можно потом из его календаря. Перенесённому тренировка группы встанет отдельной копией на выбранную дату.</p>
        </div>
        <div className="mdf">
          <span className="sp"></span>
          <button className="btn gh" onClick={() => onDone(null)}>
            Отмена
          </button>
          <button className="btn" onClick={() => onDone(ch)}>
            Опубликовать
          </button>
        </div>
      </div>
      {pick && (
        <DatePicker
          anchor={pick.el}
          value={typeof ch[conflictKey(pick.r)] === 'string' ? (ch[conflictKey(pick.r)] as string) : addDays(pick.r.date, 1)}
          onPick={(d) => {
            const r = pick.r;
            setPick(null);
            /* Тот же день — это не перенос, а замена */
            if (d === r.date) setCh((c) => ({ ...c, [conflictKey(r)]: true }));
            else moveTo(r, d);
          }}
          onClose={() => setPick(null)}
        />
      )}
    </div>,
    document.body,
  );
}
